import auth from '../Auth';
import axios from 'axios';
import { Avatar, Tooltip } from '@nextui-org/react';
import { Link } from '@nextui-org/react';
import { ReactComponent as PromptIcon } from '../icons/prompt.svg';
import { ReactComponent as BookIcon } from '../icons/book.svg';
import { ReactComponent as LogsIcon } from '../icons/logs.svg';
import { ReactComponent as SettingsIcon } from '../icons/settings.svg';
import { ReactComponent as TestsIcon } from '../icons/tests.svg';
import { ReactComponent as Play } from '../icons/playground.svg';
import { API_URL, TOAST_STYLE_SUCCESS } from '../utils/constants';
import { toast } from 'react-toastify';
import React, { useState, useEffect } from 'react';
import Logo from '../icons/Logo.png';
import Cookies from 'js-cookie';
// import { Play } from 'lucide-react';

import PlaygroundSelector from './PlaygroundSelector';

const ExpandableSidebar = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [userData, setUserData] = useState(auth.userData);
  const [prompts, setPrompts] = useState([]);
  const [referralId, setFetchedReferralId] = useState(null);
  const [isPlaygroundSelectorOpen, setIsPlaygroundSelectorOpen] = useState(false);
  let timeoutId = null;
  
  const handleMouseOver = () => {
    clearTimeout(timeoutId);
    setIsExpanded(true);
  };

  const fetchReferralId = async () => {
    if (!auth.userData || !auth.userData.email) {
      return; 
    }

    try {
      const response = await axios.get(
        `${API_URL}/referrals?email=${auth.userData.email}`,
        {
          headers: {
            Authorization: `Bearer ${await auth.getIdToken()}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const referralIdFromResponse = response.data.referral_id;
      setFetchedReferralId(referralIdFromResponse);
      Cookies.set('referral_id', referralIdFromResponse);

    } catch (error) {
      console.error('Error fetching referral ID:', error);
    }
  };

  function fetchPrompts() {
    const token = auth.syncGetIdToken();
    fetch(`${API_URL}/prompts`, {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    })
      .then((response) => response.json())
      .then((data) => {
        setPrompts(data);
      })
      .catch((error) => console.error('Error fetching prompts:', error));
  }

  const handleMouseOut = () => {
    if (isDropdownVisible) {
      return;
    }
    timeoutId = setTimeout(() => {
      setIsExpanded(false);
      setIsDropdownVisible(false);
    }, 500);
  };

  const toggleDropdown = () => {
    timeoutId = setTimeout(() => {
      setIsDropdownVisible(!isDropdownVisible);
    }, 500);
  };

  // Add keyboard shortcut for opening playground (Alt+P)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.altKey && e.key === 'p') {
        e.preventDefault(); // Prevent default browser behavior
        openPlaygroundSelector();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const openPlaygroundSelector = () => {
    setIsPlaygroundSelectorOpen(true);
  };

  const closePlaygroundSelector = () => {
    setIsPlaygroundSelectorOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cookieReferralId = Cookies.get('referral_id');

        if (cookieReferralId) {
          setFetchedReferralId(cookieReferralId);
        } else {
          await fetchReferralId();
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (auth.userData && auth.userData.email) 
      fetchPrompts();
  }, []);

  return (
    <>
      <div
        className={`flex flex-col h-screen p-4 transition-width duration-0 ${isExpanded ? 'w-40' : 'w-16'}`}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
        style={{ backgroundColor: '#E1E4E9' }}
      >
        <div className='rounded'>
          <Link
            href='/'
            style={{
              width: isExpanded ? '40px' : '40px',
              height: isExpanded ? '40px' : '40px'
            }}
          >
            <img src={Logo} alt='' width={34} />
          </Link>
        </div>
        <div className='mt-8 space-y-6'>
          {!prompts.length && (
            <div>
              <Link href='/start' className='hover:bg-gray-300 pl-1 rounded' style={{ color: 'black' }}>
                <BookIcon className='h-6 w-6 text-gray-700' />
                {isExpanded && <span className='ml-2'>Guide</span>}
              </Link>
              <br />
            </div>
          )}
          <Link href='/prompts' className='hover:bg-gray-300 pl-1 rounded' style={{ color: 'black' }}>
            <PromptIcon className='h-6 w-6 text-gray-700' />
            {isExpanded && <span className='ml-2'>Prompts</span>}
          </Link>
          <br />
          <Link href='/tests' className='hover:bg-gray-300 pl-1 rounded' style={{ color: 'black' }}>
            <TestsIcon className='h-6 w-6 text-gray-700' />
            {isExpanded && <span className='ml-2'>CI/CD</span>}
          </Link>
          <br />
          <Link href='/logs' className='hover:bg-gray-300 pl-1 rounded' style={{ color: 'black' }}>
            <LogsIcon className='h-6 w-6 text-gray-700' />
            {isExpanded && <span className='ml-2'>Logs</span>}
          </Link>
          <br />
          <Link href='/settings' className='hover:bg-gray-300 pl-1 rounded' style={{ color: 'black' }}>
            <SettingsIcon className='h-6 w-6 text-gray-700' />
            {isExpanded && <span className='ml-2'>Settings</span>}
          </Link>
          <br />
          <Link onClickCapture={openPlaygroundSelector} className='hover:bg-gray-300 pl-1 rounded cursor-pointer' style={{ color: 'black' }}>
              <Play className='h-6 w-6 text-gray-700' />
              {isExpanded && <span className='ml-2'>Playground</span>}
            </Link>
        </div>
        <div className='mt-auto flex flex-col items-center'>
          {/* Avatar and Dropdown Menu */}
          {isDropdownVisible && (
            <div className='mb-2 bg-white shadow-lg rounded-lg mt-auto p-2 ml-[40px]'>
              {userData.email && (
                <Link href='/profile' className='text-sm hover:underline text-gray-700 block'>
                  {userData.email}
                </Link>
              )}
              {referralId && (
                <div className='relative text-sm text-gray-700 cursor-pointer group whitespace-nowrap'>
                  Refer friends:&nbsp;
                  <span className='font-bold cursor-pointer text-blue-500 hover:underline'
                    onClick={() => {
                      navigator.clipboard.writeText(referralId)
                        .then(() => {
                          toast.success('Successfully copied your referral ID!', TOAST_STYLE_SUCCESS);
                        })
                        .catch(err => {
                          console.error('Failed to copy: ', err);
                        });
                    }}>
                    {referralId}
                    <span className="whitespace-normal absolute w-64 ml-12 bottom-full mb-2 left-1/2 transform -translate-x-1/2 text-xs text-white bg-black rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Send your Referral ID to your friends so they can receive one month of free usage! They should enter it during checkout.
                    </span>
                  </span>
                </div>
              )}
              <Link
                onClick={() => window.open('https://discord.gg/h463QYb7Je')}
                className='cursor-pointer text-sm hover:underline text-gray-700 block'
              >
                Contact Us
              </Link>
              <a href='/login' onClick={() => auth.logout()}  className='text-sm hover:underline text-gray-700 block'>
                Logout
              </a>
            </div>
          )}
          <Avatar
            src={(userData.avatar || '').replace('https://cloud', 'https://portal').replace('flow-prompt.com', 'lamoom.com')}
            name={userData.name || 'User Name'}
            onClick={toggleDropdown}
            className='cursor-pointer w-12 h-12'
            style={{ aspectRatio: '1 / 1' }}
          />
        </div>
      </div>
      
      {/* PlaygroundSelector Modal */}
      <PlaygroundSelector 
        isOpen={isPlaygroundSelectorOpen} 
        onClose={closePlaygroundSelector} 
      />
    </>
  );
};

export default ExpandableSidebar;